/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {getFormattedDate, getTimeSince} from '../../utils/ComponentUtils';

export interface EventTimeProps {
  eventTime: string | Date;
  padding?: string;
}

export const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    container : {
      display        : 'flex',
      justifyContent : 'center',
      flexDirection  : 'column',
      padding        : ({padding}: {padding: string}) => padding || '0px'
    },
    messageTime : {
      ...dlsAlt('typography', 'label2'),
      fontWeight    : 700,
      margin        : '2.5px 0px',
      textTransform : 'uppercase'
    },
    messageTimeDiff : {
      ...dlsTypography('body3'),
      color      : dlsColor('grey3'),
      fontWeight : 400,
      fontSize   : '12px',
      lineHeight : '16px',
      margin     : '-2px 0px'
    }
  };
});

/**
 * EventTime component is used to display the time of the event along with the time since the event.
 *
 * @example
 * By passing an eventTime of "Mon Dec 25 2023 23:59:00 GMT-0600 (Central Standard Time)"
 *
 * The component will display the following:
 * 12/25/2023 AT 11:59 PM
 * Taken 5 minutes ago
 *
 * ```typescript
 *
 * import {EventTime} from '@ion/dls/src/components/time/EventTime';
 *
 * <EventTime eventTime={eventTime} />
 *
 * ```
 *
 * @param eventTime - time of the event
 *
 * @returns {JSX.Element}
 *
 */
const EventTime = ({eventTime, padding = '0px'}: EventTimeProps) => {
  const classes = useStyles({padding});
  return (
    <div className={classes.container}>
      <p className={classes.messageTime}>
        {getFormattedDate(eventTime)}
      </p>
      <p className={classes.messageTimeDiff}>
        {getTimeSince(eventTime)}
      </p>
    </div>
  );
};

export default EventTime;
